// src/components/Contact.js
// Merged closing CTA + application section at the end of the homepage scroll
// (anchor: #contact). Dark band ("Ready to build a bigger total?") leads
// straight into the application form, which can be popped into a full-screen
// overlay for easier filling.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaInstagram, FaEnvelope, FaExpand } from "react-icons/fa";
import "./Contact.css";

const FORM_SRC = "https://forms.gle/3bMmMLyucKqXweKB8";

const Contact = () => {
  const [fullscreen, setFullscreen] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const formRef = useRef(null);
  const close = useCallback(() => setFullscreen(false), []);

  // Lazy-mount the Google Form iframe: only fetch it once the section nears the
  // viewport, so a plain homepage visit doesn't load Google Forms. Once shown it
  // stays mounted. Without IntersectionObserver (old browsers / jsdom), load it
  // immediately so the form is never missing.
  useEffect(() => {
    if (formLoaded) return undefined;
    const node = formRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setFormLoaded(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setFormLoaded(true);
          observer.disconnect();
        }
      },
      // Start loading while it's still ~a screen below the fold so it's ready.
      { rootMargin: "400px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [formLoaded]);

  // Esc to close + lock background scroll while the overlay is open.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [fullscreen, close]);

  return (
    <section id="contact" className="contact">
      <div className="contact__intro">
        <p className="contact__eyebrow">Start your prep</p>
        <h2 className="contact__title">Ready to build a bigger total?</h2>
        <p className="contact__text">
          Tell me about your goals and your lifting, and we'll map the path to
          your next platform.
        </p>
      </div>

      <div className="contact__inner">
        <div className="contact__form" ref={formRef}>
          <button
            type="button"
            className="contact__fullscreen"
            onClick={() => setFullscreen(true)}
          >
            <FaExpand aria-hidden="true" /> Full screen
          </button>
          {formLoaded ? (
            <iframe
              src={FORM_SRC}
              width="100%"
              height="600"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Coaching Application Form"
            >
              Loading…
            </iframe>
          ) : (
            <div className="contact__form-placeholder" aria-hidden="true">
              <span className="contact__form-spinner" />
              Loading application form…
            </div>
          )}
        </div>

        <p className="contact__alt">
          Got questions or want to reach out first?{" "}
          <a
            href="https://forms.gle/Ahsd2vAPGicExDSH8"
            target="_blank"
            rel="noopener noreferrer"
          >
            Send a message
          </a>
        </p>

        <div className="contact__icons">
          <a
            href="https://www.instagram.com/ssspowerlifting?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
            target="_blank"
            rel="noopener noreferrer"
            aria-label="SSS Powerlifting on Instagram"
            className="contact__icon"
          >
            <FaInstagram size={26} />
          </a>
          <a
            href="mailto:ssspowerlifting@gmail.com"
            aria-label="Email SSS Powerlifting"
            className="contact__icon"
          >
            <FaEnvelope size={26} />
          </a>
        </div>
      </div>

      {fullscreen &&
        createPortal(
          <div
            className="contact-fs"
            role="dialog"
            aria-modal="true"
            aria-label="Coaching application form"
          >
            <div className="contact-fs__bar">
              <span className="contact-fs__label">Coaching application</span>
              <button
                type="button"
                className="contact-fs__close"
                aria-label="Close full-screen form"
                onClick={close}
                autoFocus
              >
                &times;
              </button>
            </div>
            <iframe
              src={FORM_SRC}
              className="contact-fs__frame"
              title="Coaching Application Form (full screen)"
            >
              Loading…
            </iframe>
          </div>,
          document.body
        )}
    </section>
  );
};

export default Contact;
